import { useState, useEffect, useMemo, type FormEvent } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, MapPin, Users, CheckCircle, ArrowLeft, Info, AlertCircle, Share2, Mail, MessageSquare, Facebook, Instagram, ExternalLink, CreditCard, Bird } from 'lucide-react';
import { Button, Typography } from '@mui/material';
import Logo from '../components/Logo';
import { outingsService } from '../../../services/jacanaDbService';
import { Outing, Booking, type PaymentMethod } from '../types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import useFirebaseNotifications from '../../../hooks/useFirebaseNotifications';
import NotificationPrompt from '../components/NotificationPrompt';
import HelpTrigger from '../../help/components/HelpTrigger';
import { getEffectiveBankAccount, paymentService } from '../../../services/paymentService';
import { db } from '../../../services/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import TermsConsentField from '../../legal/components/TermsConsentField';
import OptionalConsentsSection from '../../legal/components/OptionalConsentsSection';
import PrivacyLinksBlock from '../../legal/components/PrivacyLinksBlock';
import {
  createEmptyConsentSelection,
  type ConsentSelection,
  getConsentErrorMessages,
  hasAcceptedCurrentMandatoryVersions,
  requiresSensitiveConsentForBooking,
  resolveBookingLegalIntent,
} from '../../legal/legalConfig';
import { getAnalyticsConsentStatus } from '../../legal/legalStorage';
import { useLegalConsentSummary } from '../../legal/useLegalConsentSummary';
import { organizationService } from '../../../services/organizationService';
import BankTransferInstructions from '../components/BankTransferInstructions';
import { BookingPricingSummary } from '../components/BankTransferInstructions';
import useAppUser from '../../../hooks/useAppUser';
import { subscribeToWaitlist, unsubscribeFromWaitlist } from '../../../services/waitlistService';
import { getUserFacingErrorMessage } from '../../../utils/userFacingError';
import {
  createInitialBookingRateQuantities,
  getBookingFormPricing,
  requiresPaymentStep,
} from '../pricing/bookingFormPricing';
import { getOutingPricingDisplay, getStartingPrice } from '../pricing/pricingDisplay';
import {
  type BookingCommunicationSnapshot,
  readBookingCommunicationSnapshot,
  resolveDisplayedBookingSnapshot,
} from './bookingCommunicationSnapshot';

type ReturnBookingInfoState = Pick<Booking, 'userName' | 'userEmail'> & BookingCommunicationSnapshot;

type InitiateBookingDisplayResponse = Awaited<ReturnType<typeof paymentService.initiateBooking>> & Partial<BookingCommunicationSnapshot>;
type PublicBookingStatusDisplayResponse = Awaited<ReturnType<typeof paymentService.getPublicBookingStatus>> & Partial<BookingCommunicationSnapshot>;
type BookingWithCommunicationSnapshot = Booking & {
  bookingLines?: BookingCommunicationSnapshot['bookingLines'];
  participantTotals?: { initialParticipants?: number };
  moneyTotals?: BookingCommunicationSnapshot['moneyTotals'];
};

export default function OutingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returningBookingId = new URLSearchParams(location.search).get('bookingId');
  const returningPaymentId = new URLSearchParams(location.search).get('paymentId');
  const { uid, email: appUserEmail, displayName: appUserDisplayName } = useAppUser();
  const { data: legalSummary, isFetched: isLegalSummaryFetched } = useLegalConsentSummary();
  const { permissionStatus, requestPermission } = useFirebaseNotifications();
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [outing, setOuting] = useState<Outing | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [paymentStep, setPaymentStep] = useState<'form' | 'mollie' | 'bank_transfer' | 'verifying' | 'success'>(() => returningBookingId ? 'verifying' : 'form');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [rateQuantitiesState, setRateQuantitiesState] = useState<{
    outingId: string | null;
    quantities: Record<string, number>;
  }>({ outingId: null, quantities: {} });
  
  // Form state
  const [formData, setFormData] = useState({
    userName: '',
    userEmail: '',
    userPhone: '',
    participantsCount: 1,
    specialNeeds: '',
    paymentMethod: 'mollie' as 'mollie' | 'bank_transfer' | 'on_site'
  });

  const [bankCommunication, setBankCommunication] = useState('');
  const [bankTransferIban, setBankTransferIban] = useState<string | null>(null);
  const [returnBookingInfo, setReturnBookingInfo] = useState<ReturnBookingInfoState | null>(null);
  const [verificationMessage, setVerificationMessage] = useState(() => returningBookingId ? "Nous vérifions la confirmation de votre paiement auprès de Mollie..." : "Nous vérifions la confirmation de votre paiement auprès de Mollie...");
  const [verificationHint, setVerificationHint] = useState(() => returningBookingId ? '' : '');
  const [consentSelection, setConsentSelection] = useState(() => {
    const selection = createEmptyConsentSelection();
    selection.analyticsCookiesAccepted = getAnalyticsConsentStatus() === 'accepted';
    return selection;
  });
  const [consentErrors, setConsentErrors] = useState<string[]>([]);
  const [optionalPreferencesVisibility, setOptionalPreferencesVisibility] = useState<boolean | null>(null);
  const [hasUserEditedOptionalConsents, setHasUserEditedOptionalConsents] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({
    displayName: '',
    email: '',
    notifyByEmail: true,
    notifyByPush: false,
    consentAccepted: false,
  });
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistFeedback, setWaitlistFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [waitlistSubscribed, setWaitlistSubscribed] = useState(false);
  const [waitlistEntryId, setWaitlistEntryId] = useState<string | null>(null);
  const isAuthenticated = Boolean(uid);
  const canOfferPushWaitlist = isAuthenticated && permissionStatus === 'granted';
  const hasCurrentMandatoryAcceptance = hasAcceptedCurrentMandatoryVersions(legalSummary);
  const shouldShowMandatoryLegalCheckboxes = !isAuthenticated || !hasCurrentMandatoryAcceptance;
  const requiresSpecialCategoryConsent = requiresSensitiveConsentForBooking(formData.specialNeeds);
  const hasKnownMarketingPreference = typeof legalSummary?.marketingEmailAccepted === 'boolean';
  const hasKnownAnalyticsPreferenceFromSummary = typeof legalSummary?.analyticsCookiesAccepted === 'boolean';
  const analyticsStatusFromStorage = getAnalyticsConsentStatus();
  const hasKnownAnalyticsPreferenceFromStorage = analyticsStatusFromStorage === 'accepted' || analyticsStatusFromStorage === 'rejected';
  const hasKnownOptionalPreferences =
    hasKnownMarketingPreference || hasKnownAnalyticsPreferenceFromSummary || hasKnownAnalyticsPreferenceFromStorage;
  const resolvedKnownAnalyticsPreference = hasKnownAnalyticsPreferenceFromSummary
    ? legalSummary?.analyticsCookiesAccepted === true
    : hasKnownAnalyticsPreferenceFromStorage
      ? analyticsStatusFromStorage === 'accepted'
      : null;
  const bookingLegalIntent = resolveBookingLegalIntent({
    isAuthenticated,
    summary: legalSummary,
  });

  const effectiveConsentSelection = useMemo<ConsentSelection>(() => {
    if (hasUserEditedOptionalConsents) {
      return consentSelection;
    }

    let next = consentSelection;

    if (hasKnownMarketingPreference && next.marketingEmailAccepted !== (legalSummary?.marketingEmailAccepted === true)) {
      next = {
        ...next,
        marketingEmailAccepted: legalSummary?.marketingEmailAccepted === true,
      };
    }

    if (
      typeof resolvedKnownAnalyticsPreference === 'boolean' &&
      next.analyticsCookiesAccepted !== resolvedKnownAnalyticsPreference
    ) {
      next = {
        ...next,
        analyticsCookiesAccepted: resolvedKnownAnalyticsPreference,
      };
    }

    return next;
  }, [
    consentSelection,
    hasKnownMarketingPreference,
    hasUserEditedOptionalConsents,
    legalSummary?.marketingEmailAccepted,
    resolvedKnownAnalyticsPreference,
  ]);

  const initialShowOptionalPreferences = analyticsStatusFromStorage === 'unset';
  const showOptionalPreferences = optionalPreferencesVisibility ?? (
    isAuthenticated && !isLegalSummaryFetched && !hasKnownAnalyticsPreferenceFromStorage
      ? initialShowOptionalPreferences
      : !hasKnownOptionalPreferences
  );
  const effectiveWaitlistDisplayName = waitlistForm.displayName || appUserDisplayName || '';
  const effectiveWaitlistEmail = waitlistForm.email || appUserEmail || '';
  const effectiveWaitlistNotifyByPush = canOfferPushWaitlist ? waitlistForm.notifyByPush : false;

  const handleConsentSelectionChange = (nextValue: ConsentSelection) => {
    if (
      nextValue.marketingEmailAccepted !== effectiveConsentSelection.marketingEmailAccepted ||
      nextValue.analyticsCookiesAccepted !== effectiveConsentSelection.analyticsCookiesAccepted
    ) {
      setHasUserEditedOptionalConsents(true);
    }

    setConsentSelection(nextValue);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id]);

  const rateQuantities = useMemo(() => {
    const outingId = outing?.id ?? null;
    if (rateQuantitiesState.outingId === outingId) {
      return rateQuantitiesState.quantities;
    }

    return createInitialBookingRateQuantities(outing?.pricingConfig);
  }, [outing?.id, outing?.pricingConfig, rateQuantitiesState]);

  useEffect(() => {
    console.info('[OutingDetail] mount', {
      pathname: location.pathname,
      search: location.search,
      id,
      userAgent: navigator.userAgent,
    });
  }, [id, location.pathname, location.search]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let verificationTimeout: number | undefined;
    let publicStatusPollInterval: number | undefined;

    async function load() {
      if (!id) {
        console.warn('[OutingDetail] missing outing id for route', {
          pathname: location.pathname,
          search: location.search,
        });
        setLoadError("missing_outing_id");
        setLoading(false);
        return;
      }

      try {
        setLoadError(null);
        const [data, organization] = await Promise.all([
          outingsService.getById(id),
          organizationService.getById().catch(() => null),
        ]);

        if (!data) {
          console.warn('[OutingDetail] outing not found', {
            id,
            pathname: location.pathname,
          });
          setLoadError("outing_not_found");
        }

        setOuting(data);
        setBankTransferIban(getEffectiveBankAccount({ organization, outing: data }));
        if (data && (data.paymentMethods || []).length > 0) {
          setFormData(prev => ({ ...prev, paymentMethod: data.paymentMethods![0] }));
        }
      } catch (error) {
        console.error("Failed to load outing", {
          id,
          pathname: location.pathname,
          userAgent: navigator.userAgent,
          error,
        });
        setLoadError("outing_load_failed");
        toast.error("Erreur lors du chargement de l'expédition");
      } finally {
        setLoading(false);
      }
    }

    load();

    if (returningBookingId) {
      verificationTimeout = window.setTimeout(() => {
        setVerificationMessage("Validation toujours en cours");
        setVerificationHint("Le webhook Mollie ou l'accès à la réservation peut être en retard. N'effectuez pas un nouveau paiement. Cette page continuera à vérifier automatiquement.");
      }, 15000);

      const handleBookingStatus = (resolvedBookingStatus: string | undefined, resolvedPaymentStatus: string | undefined) => {
        if (resolvedBookingStatus === 'confirmed' || resolvedPaymentStatus === 'paid' || resolvedPaymentStatus === 'refunded') {
          if (verificationTimeout) window.clearTimeout(verificationTimeout);
          if (publicStatusPollInterval) window.clearInterval(publicStatusPollInterval);
          toast.success("Paiement validé ! Votre réservation est confirmée.");
          setPaymentStep('success');
          navigate(location.pathname, { replace: true });
          return;
        }

        if (resolvedBookingStatus && ['failed', 'cancelled', 'expired'].includes(resolvedBookingStatus)) {
          if (verificationTimeout) window.clearTimeout(verificationTimeout);
          if (publicStatusPollInterval) window.clearInterval(publicStatusPollInterval);
          toast.error(`Paiement ${resolvedBookingStatus === 'expired' ? 'expiré' : 'échoué'}.`);
          setPaymentStep('form');
          navigate(location.pathname, { replace: true });
          return;
        }

        setPaymentStep('verifying');
        setVerificationMessage("Validation en cours");
      };

      if (isAuthenticated) {
        unsubscribe = onSnapshot(doc(db, 'bookings', returningBookingId), (docSnapshot) => {
          if (!docSnapshot.exists()) {
            console.error("Returned booking not found yet:", returningBookingId);
            setPaymentStep('verifying');
            setVerificationMessage("Réservation introuvable pour le moment");
            setVerificationHint("Le retour Mollie a bien eu lieu, mais la réservation n'est pas encore lisible. N'effectuez pas un nouveau paiement et réessayez d'actualiser cette page dans quelques instants.");
            return;
          }

          const bookingData = docSnapshot.data() as BookingWithCommunicationSnapshot;
          setReturnBookingInfo({
            userName: bookingData.userName,
            userEmail: bookingData.userEmail,
            ...(readBookingCommunicationSnapshot({
              participantsCount: bookingData.participantTotals?.initialParticipants ?? bookingData.participantsCount,
              amount: bookingData.moneyTotals?.initialTotal ?? bookingData.amount,
              bookingLines: bookingData.bookingLines,
              moneyTotals: bookingData.moneyTotals,
            }) || { participantsCount: 0, amount: 0 })
          });

          const resolvedBookingStatus = bookingData.bookingStatus || bookingData.status;
          const resolvedPaymentStatus = bookingData.paymentStatus || (bookingData.isPaid ? 'paid' : 'pending');
          handleBookingStatus(resolvedBookingStatus, resolvedPaymentStatus);
        }, (err) => {
          console.error("Error listening to booking status", err);
          setPaymentStep('verifying');
          setVerificationMessage("Impossible de confirmer le paiement pour l'instant");
          setVerificationHint("L'accès à la réservation a échoué ou le webhook est en retard. N'effectuez pas un nouveau paiement. Réessayez d'actualiser cette page dans quelques instants.");
          setLoading(false);
        });
      } else {
        const pollPublicBookingStatus = async () => {
          try {
            const status = await paymentService.getPublicBookingStatus({
              bookingId: returningBookingId,
              outingId: id,
              paymentId: returningPaymentId || undefined,
            }) as PublicBookingStatusDisplayResponse;

            const publicSnapshot = readBookingCommunicationSnapshot(status);
            if (publicSnapshot) {
              setReturnBookingInfo((current) => ({
                userName: current?.userName || '',
                userEmail: current?.userEmail || '',
                ...publicSnapshot,
              }));
            }

            handleBookingStatus(status.bookingStatus, status.paymentStatus);
          } catch (err) {
            console.error('Error fetching public booking status', err);
            setPaymentStep('verifying');
            setVerificationMessage("Impossible de confirmer le paiement pour l'instant");
            setVerificationHint("Le webhook Mollie peut être en retard. N'effectuez pas un nouveau paiement et réessayez d'actualiser cette page dans quelques instants.");
          }
        };

        void pollPublicBookingStatus();
        publicStatusPollInterval = window.setInterval(() => {
          void pollPublicBookingStatus();
        }, 3000);
      }
    }

    return () => {
      if (unsubscribe) unsubscribe();
      if (verificationTimeout) window.clearTimeout(verificationTimeout);
      if (publicStatusPollInterval) window.clearInterval(publicStatusPollInterval);
    };
  }, [id, returningBookingId, returningPaymentId, isAuthenticated, navigate, location.pathname, location.search]);

  const buildOutingShareText = (outingData: Outing, publicShareUrl: string) => {
    const dateFormatted = format(new Date(outingData.date), 'd MMMM yyyy', { locale: fr });
    const normalizedDescription = (outingData.description || '').replace(/\s+/g, ' ').trim();
    const shortDescription = normalizedDescription.length > 180
      ? `${normalizedDescription.slice(0, 177).trimEnd()}...`
      : normalizedDescription;
    const startingPrice = getStartingPrice(outingData.pricingConfig);
    const priceLabel = startingPrice === null
      ? null
      : (startingPrice > 0 ? `${startingPrice} EUR` : 'Gratuit');

    const lines = [
      `🌿 Expédition Jacana : ${outingData.title}`,
      '',
      shortDescription,
      '',
      `🗓 Date : ${dateFormatted}`,
    ];

    if (outingData.location?.trim()) {
      lines.push(`📍 Lieu : ${outingData.location.trim()}`);
    }

    if (priceLabel) {
      lines.push(`💶 Prix : ${priceLabel}`);
    }

    lines.push('', `🔗 Lien public : ${publicShareUrl}`);

    return lines.join('\n');
  };

  const pricingDisplay = outing ? getOutingPricingDisplay(outing) : null;
  const bookingFormPricing = useMemo(
    () => outing ? getBookingFormPricing({ outing, participantsCount: formData.participantsCount, rateQuantities }) : null,
    [formData.participantsCount, outing, rateQuantities],
  );
  const bookingParticipantsCount = bookingFormPricing?.participantsCount ?? formData.participantsCount;
  const bookingTotalAmount = bookingFormPricing?.totalAmount ?? 0;
  const bookingPricingSelection = bookingFormPricing?.usesPricingConfig ? bookingFormPricing.pricingSelection : undefined;
  const shouldUsePricingCounters = bookingFormPricing?.usesPricingConfig === true;
  const bookingTotalLabel = bookingTotalAmount > 0 ? `${bookingTotalAmount} €` : 'Gratuit';
  const displayedBookingSnapshot = resolveDisplayedBookingSnapshot({
    snapshot: returnBookingInfo,
    legacyParticipantsCount: bookingParticipantsCount,
    legacyAmount: bookingTotalAmount,
  });

  const updateRateQuantity = (rateId: string, nextQuantity: number) => {
    setRateQuantitiesState((current) => {
      const outingId = outing?.id ?? null;
      const currentQuantities = current.outingId === outingId
        ? current.quantities
        : createInitialBookingRateQuantities(outing?.pricingConfig);
      const normalizedNextQuantity = Math.max(0, Math.trunc(Number(nextQuantity) || 0));
      const totalWithoutCurrentRate = (bookingFormPricing?.rateOptions || []).reduce((sum, rate) => {
        if (rate.rateId === rateId) {
          return sum;
        }

        return sum + Math.max(0, Math.trunc(Number(currentQuantities[rate.rateId]) || 0));
      }, 0);
      const clampedQuantity = Math.min(normalizedNextQuantity, Math.max(0, spotsLeft - totalWithoutCurrentRate));

      return {
        outingId,
        quantities: {
          ...currentQuantities,
          [rateId]: clampedQuantity,
        },
      };
    });
  };

  const handleShare = async (method: string) => {
    if (!outing) return;
    const shareUrl = `${window.location.origin}/share/outing/${outing.id}`;
    const outingUrl = `${window.location.origin}/outing/${outing.id}`;
    const text = buildOutingShareText(outing, shareUrl);
    
    if (method === 'facebook') {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Texte copié. Dans Facebook, colle-le dans ton post si le texte n'apparaît pas automatiquement.", { duration: 6000 });
      } catch (e) {
        console.error("Clipboard copy failed", e);
      }
      // Note: We share the special SEO URL to Facebook so it generates a rich preview card
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (method === 'instagram') {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Texte copié. Créez un post Instagram et collez la description.", { duration: 6000, icon: '📸' });
      } catch (e) {
        console.error("Clipboard copy failed", e);
      }
    } else if (method === 'email') {
      window.location.href = `mailto:?subject=${encodeURIComponent(`Expédition Jacana : ${outing.title}`)}&body=${encodeURIComponent(text + '\n' + outingUrl)}`;
    } else if (method === 'sms') {
      window.location.href = `sms:?body=${encodeURIComponent(text + ' ' + outingUrl)}`;
    } else if (method === 'copy_text') {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Texte + lien copiés !", { duration: 5000 });
      } catch (e) {
        console.error("Clipboard copy failed", e);
      }
    } else if (method === 'copy') {
      navigator.clipboard.writeText(outingUrl);
      toast.success("Lien copié !");
    }
    setShowShareMenu(false);
  };

  const triggerNativeShare = async () => {
    if (!outing) return;
    
    const shareUrl = `${window.location.origin}/share/outing/${outing.id}`;
    const text = buildOutingShareText(outing, shareUrl);

    if (navigator.share) {
      try {
        // Many apps (like Facebook) ignore the 'text' field and only take the URL.
        // We use the SEO URL to ensure a rich preview card.
        const shareData = {
          title: `Expédition Jacana : ${outing.title}`,
          text: text,
          url: shareUrl
        };

        if (navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(text);
          } catch (clipErr) {
            console.warn("Could not copy to clipboard", clipErr);
          }
        }

        await navigator.share(shareData);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setShowShareMenu(true);
        }
      }
    } else {
      setShowShareMenu(!showShareMenu);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!outing || !id) return;

    if (returningBookingId) {
      setPaymentStep('verifying');
      return;
    }

    if (bookingParticipantsCount <= 0) {
      toast.error('Sélectionnez au moins un participant.');
      return;
    }

    if (outing.currentParticipants + bookingParticipantsCount > outing.maxParticipants) {
      toast.error("Il n'y a plus assez de places disponibles.");
      return;
    }

    const selectionForValidation = shouldShowMandatoryLegalCheckboxes
      ? effectiveConsentSelection
      : {
          ...effectiveConsentSelection,
          termsAccepted: true,
          privacyAcknowledged: true,
        };

    const errors = getConsentErrorMessages(selectionForValidation, { requiresSpecialCategoryConsent });
    if (errors.length > 0) {
      setConsentErrors(errors);
      toast.error(errors[0]);
      return;
    }

    setConsentErrors([]);

    if (requiresPaymentStep({ paymentMethod: effectivePaymentMethod, totalAmount: bookingTotalAmount })) {
      setPaymentStep('mollie');
    } else {
      await processBooking();
    }
  };

  const processBooking = async () => {
    if (!outing || !id || returningBookingId || bookingLoading) return;
    setBookingLoading(true);

    const requiresPaymentUiStep = requiresPaymentStep({
      paymentMethod: effectivePaymentMethod,
      totalAmount: bookingTotalAmount,
    });
    const shouldExpectMollieRedirect = effectivePaymentMethod === 'mollie' && requiresPaymentUiStep;
    if (shouldExpectMollieRedirect) {
      toast.loading("Redirection vers la plateforme de paiement sécurisée...", { id: "mollie-toast" });
    }
    
    try {
      const returnUrl = `${window.location.origin}/outing/${id}`;
      const shouldSendLegalAcceptance = bookingLegalIntent !== 'reuse_existing_acceptance' || requiresSpecialCategoryConsent;
      
      const response = await paymentService.initiateBooking({
        outingId: id,
        userName: formData.userName,
        userEmail: formData.userEmail,
        userPhone: formData.userPhone,
        participantsCount: bookingParticipantsCount,
        pricingSelection: bookingPricingSelection,
        specialNeeds: formData.specialNeeds,
        paymentMethod: effectivePaymentMethod,
        returnUrl: returnUrl,
        legalContext: {
          intent: bookingLegalIntent,
          requiresSensitiveConsent: requiresSpecialCategoryConsent,
          clientObservedNeedsReacceptance: shouldShowMandatoryLegalCheckboxes,
        },
        legalAcceptance: shouldSendLegalAcceptance ? effectiveConsentSelection : undefined,
      }) as InitiateBookingDisplayResponse;

      const createdBookingSnapshot = readBookingCommunicationSnapshot(response);
      if (createdBookingSnapshot) {
        setReturnBookingInfo({
          userName: formData.userName,
          userEmail: formData.userEmail,
          ...createdBookingSnapshot,
        });
      }

      if (!response.success) {
        throw new Error("Initialisation de la réservation échouée");
      }

      if (bookingTotalAmount <= 0) {
        setPaymentStep('success');
        toast.success("Réservation confirmée !");
        return;
      }

      // Handling Mollie direct redirect
      if (response.paymentMethod === 'mollie' && response.checkoutUrl) {
        window.location.href = response.checkoutUrl!;
        return; 
      }

      // Handling Bank Transfer
      if (response.paymentMethod === 'bank_transfer') {
        setBankCommunication(response.bankTransferCommunication || '');
        setBankTransferIban(response.bankTransferIban || bankTransferIban);
        setPaymentStep('bank_transfer');
        toast.success("Demande enregistrée !");
      } else if (response.paymentMethod === 'on_site' || response.paymentMethod === 'mollie') {
        setPaymentStep('success');
        toast.success("Réservation confirmée !");
      }

      if (permissionStatus === 'default') {
        setTimeout(() => setShowNotificationPrompt(true), 1500);
      }
    } catch (error: unknown) {
      console.error("Booking process failed", error);
      toast.dismiss("mollie-toast");
      toast.error(getUserFacingErrorMessage(error, "La réservation n'a pas pu être finalisée. Réessayez dans quelques instants."));
      setPaymentStep('form');
    } finally {
      if (!shouldExpectMollieRedirect) {
        toast.dismiss("mollie-toast");
      }
      setBookingLoading(false);
    }
  };

  const getErrorCode = (error: unknown) => {
    if (typeof error === 'object' && error !== null && 'code' in error) {
      return String((error as { code?: unknown }).code || '').toLowerCase();
    }

    return '';
  };

  const handleWaitlistSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!id || waitlistLoading) return;

    const displayName = effectiveWaitlistDisplayName.trim();
    const email = effectiveWaitlistEmail.trim();

    if (!displayName || !email) {
      setWaitlistFeedback({
        type: 'error',
        message: 'Merci de remplir votre nom et votre e-mail.',
      });
      return;
    }

    if (!waitlistForm.notifyByEmail && !(canOfferPushWaitlist && effectiveWaitlistNotifyByPush)) {
      setWaitlistFeedback({
        type: 'error',
        message: 'Choisissez au moins un canal de notification.',
      });
      return;
    }

    if (!waitlistForm.consentAccepted) {
      setWaitlistFeedback({
        type: 'error',
        message: 'Le consentement est obligatoire pour créer l\'alerte.',
      });
      return;
    }

    setWaitlistLoading(true);
    setWaitlistFeedback(null);

    try {
      const response = await subscribeToWaitlist({
        outingId: id,
        displayName,
        email,
        notifyByEmail: waitlistForm.notifyByEmail,
        notifyByPush: effectiveWaitlistNotifyByPush,
        consent: {
          granted: true,
          textVersion: 'waitlist_v1',
          source: 'outing_detail_ui',
        },
      });

      if (response.status === 'subscribed') {
        setWaitlistSubscribed(true);
        setWaitlistEntryId(response.entryId || null);
        setWaitlistFeedback({
          type: 'success',
          message: 'Alerte créée. Nous vous préviendrons si une place se libère.',
        });
        return;
      }

      if (response.status === 'already_subscribed') {
        setWaitlistSubscribed(false);
        setWaitlistEntryId(null);
        setWaitlistFeedback({
          type: 'info',
          message: 'Vous êtes déjà inscrit à l’alerte pour cette sortie.',
        });
        return;
      }

      setWaitlistFeedback({
        type: 'error',
        message: 'Impossible de créer l’alerte pour le moment.',
      });
    } catch (error: unknown) {
      const code = getErrorCode(error);
      if (code.includes('failed-precondition')) {
        setWaitlistFeedback({
          type: 'info',
          message: 'La disponibilité a changé. Rechargez la page et réservez directement si des places sont revenues.',
        });
      } else if (code.includes('invalid-argument')) {
        setWaitlistFeedback({
          type: 'error',
          message: 'Informations invalides. Vérifiez le formulaire.',
        });
      } else {
        setWaitlistFeedback({
          type: 'error',
          message: 'Une erreur est survenue. Réessayez dans quelques instants.',
        });
      }
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleWaitlistUnsubscribe = async () => {
    if (!id || !isAuthenticated || waitlistLoading) return;

    if (!waitlistEntryId) {
      setWaitlistFeedback({
        type: 'error',
        message: 'Impossible de déterminer l’alerte à supprimer depuis cette session.',
      });
      return;
    }

    setWaitlistLoading(true);
    setWaitlistFeedback(null);

    try {
      const response = await unsubscribeFromWaitlist({
        outingId: id,
        entryId: waitlistEntryId,
      });

      if (response.status === 'unsubscribed' || response.status === 'already_unsubscribed') {
        setWaitlistSubscribed(false);
        setWaitlistEntryId(null);
        setWaitlistFeedback({
          type: 'info',
          message: 'Vous êtes désinscrit de cette alerte.',
        });
        return;
      }

      setWaitlistFeedback({
        type: 'error',
        message: 'Impossible de vous désinscrire pour le moment.',
      });
    } catch (error: unknown) {
      const code = getErrorCode(error);
      if (code.includes('invalid-argument')) {
        setWaitlistFeedback({
          type: 'error',
          message: 'Paramètres invalides pour la désinscription.',
        });
      } else if (code.includes('permission-denied')) {
        setWaitlistFeedback({
          type: 'error',
          message: 'Vous ne pouvez pas vous désinscrire de cette alerte depuis cette session.',
        });
      } else {
        setWaitlistFeedback({
          type: 'error',
          message: 'Une erreur est survenue. Réessayez dans quelques instants.',
        });
      }
    } finally {
      setWaitlistLoading(false);
    }
  };

  const isFacebookWebView = /FBAN|FBAV|FB_IAB|Instagram/i.test(navigator.userAgent);
  if (isFacebookWebView) {
    const currentUrl = window.location.href;
    const externalShareUrl = id ? `${window.location.origin}/share/outing/${id}` : currentUrl;
    const isAndroid = /android/i.test(navigator.userAgent);
    const intentUrl = isAndroid
      ? `intent://${window.location.host}${window.location.pathname}#Intent;scheme=https;action=android.intent.action.VIEW;end`
      : null;
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-brand-paper px-6 text-center space-y-6">
        <div className="text-5xl">🌿</div>
        <h1 className="font-serif italic text-2xl text-brand-primary">Ouvre cette page dans ton navigateur</h1>
        <p className="text-brand-primary/70 text-sm leading-relaxed">
          Cette page ne fonctionne pas correctement dans le navigateur intégré de Facebook.
          Pour accéder à la réservation, ouvre-la dans ton navigateur habituel.
        </p>
        {intentUrl ? (
          <a
            href={intentUrl}
            className="btn-premium"
          >
            Ouvrir dans Chrome
          </a>
        ) : (
          <p className="text-brand-primary/60 text-sm leading-relaxed max-w-sm">
            Sur iPhone, appuie sur ··· puis choisis « Ouvrir dans Safari ».
          </p>
        )}
        <button
          onClick={() => {
            navigator.clipboard.writeText(externalShareUrl);
            toast.success("Lien public copié !", { duration: 5000 });
          }}
          className="btn-premium"
        >
          Copier le lien public
        </button>
        <p className="text-brand-primary/50 text-xs">
          {isAndroid ? 'Ou appuie sur ⋮ puis « Ouvrir dans Chrome »' : 'Ou appuie sur ··· puis « Ouvrir dans Safari »'}
        </p>
      </div>
    );
  }

  if (loading) return (
    <div className="flex justify-center items-center min-h-[100dvh] bg-brand-paper">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent"></div>
    </div>
  );

  if (loadError) return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-brand-paper space-y-8 px-6 text-center">
      <p className="font-serif italic text-2xl text-brand-primary/70">Impossible de charger cette expédition.</p>
      <p className="text-brand-primary/60 text-sm max-w-md">
        Vérifie le lien partagé ou réessaie dans ton navigateur habituel.
      </p>
      <button onClick={() => navigate('/')} className="btn-premium">Retour à l'accueil</button>
    </div>
  );
  
  if (!outing) return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-brand-paper space-y-8">
      <p className="font-serif italic text-2xl text-brand-primary/40">Expédition introuvable.</p>
      <button onClick={() => navigate('/')} className="btn-premium">Retour à l'accueil</button>
    </div>
  );

  const spotsLeft = outing.maxParticipants - outing.currentParticipants;
  const renderPracticalInfoValue = (value: boolean | undefined, positiveLabel = 'Oui') => {
    if (typeof value !== 'boolean') return 'Non renseigné';
    return value ? positiveLabel : 'Non';
  };
  const now = new Date();
  const outingDate = new Date(outing.date);
  const DEFAULT_LATE_BOOKING_HOURS_BEFORE = 4;
  const hasValidLateBookingHours =
    typeof outing.lateBookingHoursBefore === 'number'
    && outing.lateBookingHoursBefore > 0
    && outing.lateBookingHoursBefore < 24;
  const hoursBeforeCloseApplied = outing.allowLateBookings
    ? (hasValidLateBookingHours ? outing.lateBookingHoursBefore : DEFAULT_LATE_BOOKING_HOURS_BEFORE)
    : 24;
  const effectiveBookingCloseAt = new Date(outingDate.getTime() - hoursBeforeCloseApplied * 60 * 60 * 1000);
  const lateBookingWindowStartsAt = new Date(outingDate.getTime() - 24 * 60 * 60 * 1000);
  const isLateBookingActive = Boolean(outing.allowLateBookings)
    && now >= lateBookingWindowStartsAt
    && now < effectiveBookingCloseAt;
  const isBookingClosed = now >= effectiveBookingCloseAt;
  const availablePaymentMethods = isLateBookingActive
    ? ['mollie'] satisfies PaymentMethod[]
    : Array.from(new Set<PaymentMethod>(outing.paymentMethods || ['mollie']));
  const effectivePaymentMethod = isLateBookingActive && availablePaymentMethods.includes('mollie')
    ? 'mollie'
    : formData.paymentMethod;
  const canShowMobileBookingCta = paymentStep === 'form' && spotsLeft > 0 && !isBookingClosed;
  const successName = (formData.userName || returnBookingInfo?.userName || '').trim();
  const successEmail = (formData.userEmail || returnBookingInfo?.userEmail || '').trim();

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.8 }}
      className="max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-28 lg:pb-12"
    >
      {/* Navigation & Header */}
      <div className="flex justify-between items-center mb-20 relative z-20">
        <button 
          onClick={() => navigate(-1)} 
          className="group flex items-center gap-4 text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] text-brand-primary opacity-70 hover:opacity-100 transition-all font-sans min-h-[44px]"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-2 transition-transform duration-500" />
          Retour
        </button>
        
        <div className="flex items-center gap-12 font-sans">
          <div className="relative">
            <button 
              onClick={triggerNativeShare}
              className="flex items-center gap-3 text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] text-brand-primary opacity-70 hover:opacity-100 transition-all font-sans min-h-[44px]"
            >
              <Share2 size={14} />
              Partager
            </button>
            
            <AnimatePresence>
              {showShareMenu && (
                <motion.div 
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute right-0 mt-8 bg-white border border-brand-primary/5 rounded-[32px] shadow-2xl p-8 w-64 z-50 space-y-4"
                >
                  <button onClick={() => handleShare('facebook')} className="flex items-center gap-4 w-full text-left text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] hover:bg-brand-paper p-4 rounded-2xl transition-all min-h-[44px]">
                    <Facebook size={14} className="opacity-40" /> Facebook
                  </button>
                  <button onClick={() => handleShare('instagram')} className="flex items-center gap-4 w-full text-left text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] hover:bg-brand-paper p-4 rounded-2xl transition-all min-h-[44px]">
                    <Instagram size={14} className="opacity-40" /> Instagram
                  </button>
                  <button onClick={() => handleShare('email')} className="flex items-center gap-4 w-full text-left text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] hover:bg-brand-paper p-4 rounded-2xl transition-all min-h-[44px]">
                    <Mail size={14} className="opacity-40" /> Email
                  </button>
                  <button onClick={() => handleShare('sms')} className="flex items-center gap-4 w-full text-left text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] hover:bg-brand-paper p-4 rounded-2xl transition-all min-h-[44px]">
                    <MessageSquare size={14} className="opacity-40" /> SMS
                  </button>
                  <button onClick={() => handleShare('copy_text')} className="flex items-center gap-4 w-full text-left text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] hover:bg-brand-paper p-4 rounded-2xl transition-all min-h-[44px]">
                    <ExternalLink size={14} className="opacity-40" /> Copier le texte + le lien
                  </button>
                  <button onClick={() => handleShare('copy')} className="flex items-center gap-4 w-full text-left text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] hover:bg-brand-paper p-4 rounded-2xl transition-all min-h-[44px]">
                    <ExternalLink size={14} className="opacity-40" /> Copier le lien
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 lg:gap-32">
        {/* Left Column: Content */}
        <div className="lg:col-span-7 space-y-32">
          <section className="space-y-16">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative aspect-[16/10] rounded-[80px] overflow-hidden border border-brand-primary/5 shadow-2xl group"
            >
              <img 
                src={outing.imageUrl || `https://picsum.photos/seed/${outing.id}/1200/800`} 
                alt={outing.title} 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[5s] ease-out"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = `https://picsum.photos/seed/${outing.id}/1200/800`;
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/40 via-transparent to-transparent" />
            </motion.div>
            
            <div className="space-y-10">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="inline-flex items-center gap-4 bg-brand-accent/10 text-brand-accent px-8 py-3 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.2em] font-sans"
              >
                <Bird size={16} />
                Niveau {outing.difficulty}
              </motion.div>
              <h1 className="text-7xl md:text-[100px] font-serif font-bold leading-[0.85] tracking-tighter italic text-brand-primary">
                {outing.title}
              </h1>
              <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-accent font-sans">
                {isBookingClosed
                  ? 'Réservations clôturées'
                  : isLateBookingActive
                    ? 'Inscriptions de dernière minute ouvertes'
                    : 'Réservations ouvertes'}
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-12 border-y border-brand-primary/10 py-20 font-serif">
              <div className="space-y-3">
                <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-55 text-brand-primary font-sans">Date</span>
                <p className="text-xl sm:text-2xl md:text-3xl italic text-brand-primary">{format(new Date(outing.date), 'dd MMM yyyy', { locale: fr })}</p>
              </div>
              <div className="space-y-3">
                <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-55 text-brand-primary font-sans">Durée</span>
                <p className="text-xl sm:text-2xl md:text-3xl italic text-brand-primary">{outing.duration}</p>
              </div>
              <div className="space-y-3">
                <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-55 text-brand-primary font-sans">Lieu</span>
                <p className="text-xl sm:text-2xl md:text-3xl italic text-brand-primary">{outing.location}</p>
              </div>
              <div className="space-y-3">
                <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-55 text-brand-primary font-sans">Dispo</span>
                <p className="text-xl sm:text-2xl md:text-3xl italic text-brand-primary">{spotsLeft} places</p>
              </div>
            </div>
          </section>

          <section className="space-y-16">
            <div className="flex items-center gap-8">
              <div className="h-px flex-1 bg-brand-primary/10" />
              <h2 className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-55 text-brand-primary font-sans">L'immersion</h2>
              <div className="h-px flex-1 bg-brand-primary/10" />
            </div>
            <div className="font-serif text-3xl md:text-4xl leading-[1.6] text-brand-primary/90 whitespace-pre-line italic md:first-letter:text-8xl md:first-letter:font-bold md:first-letter:mr-6 md:first-letter:float-left md:first-letter:leading-[0.8] md:first-letter:mt-3 md:first-letter:text-brand-accent">
              {outing.description}
            </div>
          </section>

          <section className="bg-white p-16 md:p-24 rounded-[80px] border border-brand-primary/5 space-y-20 shadow-sm relative overflow-hidden">
            {/* Decorative Tracks */}
            <div className="absolute -right-10 -bottom-10 opacity-[0.03] scale-150 rotate-12 pointer-events-none">
              <Logo iconOnly />
            </div>
            
            <h2 className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-55 flex items-center gap-4 text-brand-primary font-sans">
              <Info size={18} />
              Détails de l'expédition
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-24">
              <div className="space-y-10">
                <h3 className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-60 text-brand-primary font-sans">Équipement conseillé</h3>
                <ul className="space-y-6">
                  {outing.requiredGear && outing.requiredGear.map((item, i) => (
                    <li key={i} className="flex items-start gap-6 font-serif text-2xl italic group text-brand-primary">
                      <div className="mt-3 w-2 h-2 bg-brand-accent rounded-full opacity-20 group-hover:opacity-100 transition-all duration-500 scale-50 group-hover:scale-100" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-10">
                <h3 className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-60 text-brand-primary font-sans">Rendez-vous</h3>
                <p className="font-serif text-2xl italic text-brand-primary/60 leading-relaxed">
                  Le point exact de rencontre est soigneusement sélectionné pour son calme.
                </p>
                <div className="pt-8">
                  <a 
                    href={outing.meetingPoint} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-premium inline-flex items-center gap-4"
                  >
                    Localiser le départ <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            </div>

            {pricingDisplay && pricingDisplay.detailLines.length > 0 ? (
              <div className="pt-4 border-t border-brand-primary/10 space-y-6">
                <h3 className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-60 text-brand-primary font-sans">Tarifs</h3>
                <ul className="space-y-3">
                  {pricingDisplay.detailLines.map((line) => (
                    <li key={line.label} className="flex items-center justify-between gap-6 text-[13px] md:text-sm text-brand-primary">
                      <span className="opacity-80">{line.label}</span>
                      <span className="font-semibold">{line.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="pt-4 border-t border-brand-primary/10 space-y-6">
              <h3 className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-60 text-brand-primary font-sans">Infos pratiques</h3>
              <ul className="space-y-3">
                <li className="flex items-center justify-between gap-6 text-[13px] md:text-sm text-brand-primary">
                  <span className="opacity-80">Adapte aux PMR</span>
                  <span className="font-semibold">{renderPracticalInfoValue(outing.pmrAccessible)}</span>
                </li>
                <li className="flex items-center justify-between gap-6 text-[13px] md:text-sm text-brand-primary">
                  <span className="opacity-80">Adapte aux poussettes</span>
                  <span className="font-semibold">{renderPracticalInfoValue(outing.strollerAccessible)}</span>
                </li>
                <li className="flex items-center justify-between gap-6 text-[13px] md:text-sm text-brand-primary">
                  <span className="opacity-80">Chiens acceptes</span>
                  <span className="font-semibold">{renderPracticalInfoValue(outing.dogsAllowedOnLeash, 'Oui, en laisse uniquement')}</span>
                </li>
              </ul>
            </div>
          </section>
        </div>

        {/* Right Column: Booking Widget */}
        <div className="lg:col-span-5">
          <div id="booking-widget" className="sticky top-12 bg-white p-12 md:p-16 rounded-[80px] border border-brand-primary/5 shadow-2xl flex flex-col min-h-[600px] overflow-hidden">
            {/* Decorative background logo */}
            <div className="absolute right-0 top-0 opacity-[0.02] scale-[2] pointer-events-none translate-x-1/4 -translate-y-1/4">
              <Logo iconOnly />
            </div>

            <AnimatePresence mode="wait">
              {paymentStep === 'form' && (
                <motion.div 
                  key="form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="flex-1 flex flex-col relative z-10"
                >
                  <div className="mb-20 flex justify-between items-end">
                    <div>
                      <span className="text-7xl font-serif font-bold italic leading-none text-brand-primary">{pricingDisplay?.homePrimaryLabel || 'Tarifs disponibles'}</span>
                      <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-55 block mt-4 text-brand-primary font-sans">{shouldUsePricingCounters ? 'Selon le tarif choisi' : 'Tarif par explorateur'}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-serif italic leading-none text-brand-primary">{spotsLeft}</span>
                      <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-55 block mt-4 text-brand-primary font-sans">Places libres</span>
                    </div>
                  </div>

                  {spotsLeft > 0 && !isBookingClosed ? (
                    <form onSubmit={handleSubmit} className="space-y-10 flex-1 flex flex-col">
                      <div className="space-y-8 flex-1">
                        {isLateBookingActive ? (
                          <div className="rounded-[32px] border border-brand-accent/20 bg-brand-accent/5 px-6 py-5 space-y-2">
                            <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-accent font-sans">
                              Inscriptions de dernière minute ouvertes
                            </p>
                            <p className="text-[12px] md:text-sm text-brand-primary/70 leading-relaxed">
                              Les réservations restent ouvertes jusqu'au {format(effectiveBookingCloseAt, 'dd MMM yyyy à HH:mm', { locale: fr })}.
                            </p>
                          </div>
                        ) : null}

                        <div className="group">
                          <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-75 mb-2 block group-focus-within:opacity-100 transition-opacity text-brand-primary font-sans">Identité</label>
                          <input
                            required
                            type="text"
                            name="name"
                            autoComplete="name"
                            placeholder="VOTRE NOM COMPLET"
                            className="input-premium w-full bg-transparent border-b border-brand-primary/20 py-4 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all text-brand-primary font-sans placeholder:text-brand-primary/45"
                            value={formData.userName}
                            onChange={e => setFormData({ ...formData, userName: e.target.value })}
                          />
                        </div>

                        <div className="group">
                          <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-75 mb-2 block group-focus-within:opacity-100 transition-opacity text-brand-primary font-sans">Contact</label>
                          <input
                            required
                            type="email"
                            name="email"
                            autoComplete="email"
                            placeholder="VOTRE ADRESSE EMAIL"
                            className="input-premium w-full bg-transparent border-b border-brand-primary/20 py-4 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all text-brand-primary font-sans placeholder:text-brand-primary/45"
                            value={formData.userEmail}
                            onChange={e => setFormData({ ...formData, userEmail: e.target.value })}
                          />
                        </div>

                        <div className="group">
                          <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-75 mb-2 block group-focus-within:opacity-100 transition-opacity text-brand-primary font-sans">Téléphone</label>
                          <input
                            required
                            type="tel"
                            name="tel"
                            autoComplete="tel"
                            placeholder="VOTRE NUMÉRO"
                            className="input-premium w-full bg-transparent border-b border-brand-primary/20 py-4 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all text-brand-primary font-sans placeholder:text-brand-primary/45"
                            value={formData.userPhone}
                            onChange={e => setFormData({ ...formData, userPhone: e.target.value })}
                          />
                        </div>

                        <div className="pt-8">
                          <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-75 block mb-6 text-brand-primary font-sans">Participants</label>
                          {shouldUsePricingCounters ? (
                            <div className="space-y-4">
                              {bookingFormPricing?.rateOptions.map((rate) => {
                                const quantity = Math.max(0, Math.trunc(Number(rateQuantities[rate.rateId]) || 0));
                                const priceLabel = rate.isFree ? 'Gratuit' : `${rate.unitPrice}€`;

                                return (
                                  <div key={rate.rateId} className="flex items-center justify-between gap-4 rounded-[28px] border border-brand-primary/10 px-5 py-4 bg-brand-paper/40">
                                    <div>
                                      <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] text-brand-primary font-sans">{rate.label}</p>
                                      <p className="text-[11px] text-brand-primary/60 font-sans">{priceLabel}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <button
                                        type="button"
                                        onClick={() => updateRateQuantity(rate.rateId, quantity - 1)}
                                        className="w-11 h-11 rounded-full border border-brand-primary/20 text-brand-primary font-bold hover:border-brand-primary/40 transition-all"
                                        aria-label={`Diminuer ${rate.label}`}
                                      >
                                        -
                                      </button>
                                      <span className="w-8 text-center text-[13px] md:text-sm font-bold text-brand-primary font-sans">{quantity}</span>
                                      <button
                                        type="button"
                                        onClick={() => updateRateQuantity(rate.rateId, quantity + 1)}
                                        disabled={bookingParticipantsCount >= spotsLeft}
                                        className="w-11 h-11 rounded-full border border-brand-primary/20 text-brand-primary font-bold hover:border-brand-primary/40 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                        aria-label={`Augmenter ${rate.label}`}
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}

                              <div className="rounded-[28px] border border-brand-primary/10 bg-white/70 px-5 py-4 space-y-3">
                                <div className="flex items-center justify-between gap-4 text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] text-brand-primary font-sans">
                                  <span>Participants</span>
                                  <span>{bookingParticipantsCount}</span>
                                </div>
                                <div className="flex items-center justify-between gap-4 text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] text-brand-primary font-sans">
                                  <span>Total à payer</span>
                                  <span>{bookingTotalLabel}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-4 flex-wrap">
                              {[...Array(Math.min(6, spotsLeft))].map((_, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => setFormData({ ...formData, participantsCount: index + 1 })}
                                  className={`w-14 h-14 rounded-full border-2 flex items-center justify-center text-[11px] md:text-xs font-bold transition-all duration-500 font-sans ${formData.participantsCount === index + 1 ? 'bg-brand-primary text-white border-brand-primary shadow-xl' : 'border-brand-primary/20 hover:border-brand-primary/40 text-brand-primary'}`}
                                >
                                  {index + 1}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="pt-8">
                          <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-75 block mb-6 text-brand-primary font-sans">Besoins particuliers / Allergies (optionnel)</label>
                          <textarea
                            rows={3}
                            placeholder="Avez-vous des allergies, des problèmes de mobilité ou des besoins spécifiques ?"
                            className="input-premium w-full bg-brand-primary/5 rounded-[32px] p-6 text-[15px] md:text-sm font-semibold tracking-[0.03em] focus:outline-none focus:ring-1 focus:ring-brand-accent transition-all text-brand-primary font-sans placeholder:text-brand-primary/35"
                            value={formData.specialNeeds}
                            onChange={e => setFormData({ ...formData, specialNeeds: e.target.value })}
                          />
                        </div>

                        <div className="pt-10">
                          <div className="flex items-center justify-between gap-4 mb-8">
                            <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-75 block text-brand-primary font-sans">Mode de règlement</label>
                            <HelpTrigger
                              articleId="booking.payment.methods"
                              category="booking.payment"
                              label="Aide sur les modes de règlement"
                            />
                          </div>
                          <div className="space-y-4">
                            {availablePaymentMethods.map((method) => {
                              const isDisabled = isLateBookingActive && method !== 'mollie';
                              return (
                                <label
                                  key={method}
                                  className={`flex items-center justify-between p-6 rounded-[32px] border border-brand-primary/10 transition-all duration-500 ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer group hover:bg-brand-paper'}`}
                                >
                                  <div className="flex items-center gap-5">
                                    <input
                                      type="radio"
                                      name="payment"
                                      checked={effectivePaymentMethod === method}
                                      disabled={isDisabled}
                                      onChange={() => {
                                        if (!isDisabled) {
                                          setFormData({ ...formData, paymentMethod: method });
                                        }
                                      }}
                                      className="hidden"
                                    />
                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${effectivePaymentMethod === method ? 'border-brand-accent bg-brand-accent' : (isDisabled ? 'border-brand-primary/10' : 'border-brand-primary/20 group-hover:border-brand-primary/40')}`}>
                                      {effectivePaymentMethod === method ? <div className="w-2 h-2 bg-white rounded-full" /> : null}
                                    </div>
                                    <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] font-sans text-brand-primary">
                                      {method === 'mollie'
                                        ? 'Carte Bancaire'
                                        : method === 'bank_transfer'
                                          ? 'Virement Bancaire'
                                          : 'Paiement sur place'}
                                    </span>
                                  </div>
                                  <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] opacity-55 text-brand-primary font-sans">
                                    {method === 'mollie'
                                      ? 'Mollie'
                                      : method === 'bank_transfer'
                                        ? 'Action requise'
                                        : 'Espèces/Chèque'}
                                  </span>
                                </label>
                              );
                            })}
                          </div>

                          {isLateBookingActive ? (
                            <div className="mt-6 rounded-[24px] border border-brand-accent/15 bg-brand-accent/5 px-5 py-4">
                              <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] text-brand-accent mb-2">
                                Paiement immédiat requis
                              </p>
                              <p className="text-[11px] leading-relaxed text-brand-primary/60">
                                Les réservations de dernière minute nécessitent un paiement immédiat en ligne.
                              </p>
                            </div>
                          ) : null}

                          {effectivePaymentMethod === 'bank_transfer' && !isLateBookingActive ? (
                            <motion.div
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="mt-6 p-6 bg-brand-accent/5 rounded-[24px] border border-brand-accent/10"
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] text-brand-accent mb-2">Note : Virement Bancaire</p>
                                  <p className="text-[11px] leading-relaxed text-brand-primary/60">
                                    Votre inscription sera mise en attente. Elle ne sera confirmée qu'une fois votre virement reçu. Les instructions vous seront envoyées par e-mail.
                                  </p>
                                </div>
                                <HelpTrigger
                                  articleId="booking.payment.bank_transfer"
                                  category="booking.payment"
                                  label="Aide sur le virement bancaire"
                                />
                              </div>
                            </motion.div>
                          ) : null}

                          {effectivePaymentMethod === 'on_site' && !isLateBookingActive ? (
                            <motion.div
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="mt-6 p-6 bg-brand-primary/5 rounded-[24px] border border-brand-primary/10"
                            >
                              <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] text-brand-primary mb-2">Note : Paiement sur place</p>
                              <p className="text-[11px] leading-relaxed text-brand-primary/60">
                                Réglé directement le jour de la sortie (Espèces ou Chèque).
                              </p>
                            </motion.div>
                          ) : null}
                        </div>

                        <div className="pt-6 rounded-[32px] border border-brand-primary/10 bg-white/70 p-5">
                          {shouldShowMandatoryLegalCheckboxes ? (
                            <TermsConsentField
                              value={effectiveConsentSelection}
                              onChange={handleConsentSelectionChange}
                              errorMessages={consentErrors}
                            />
                          ) : (
                            <div className="space-y-3">
                              <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] text-brand-primary font-sans">
                                Vos CGV et votre politique de confidentialite sont deja a jour pour ce compte.
                              </p>
                              <p className="text-[11px] md:text-xs text-brand-primary/70 leading-relaxed">
                                Rappel non bloquant: vous pouvez consulter a tout moment les documents en vigueur avant de finaliser cette reservation.
                              </p>
                              <PrivacyLinksBlock compact />
                            </div>
                          )}
                          <div className="mt-4">
                            {showOptionalPreferences ? (
                              <div className="space-y-3 rounded-3xl border border-brand-primary/10 bg-brand-paper/40 p-4">
                                <OptionalConsentsSection
                                  value={effectiveConsentSelection}
                                  onChange={handleConsentSelectionChange}
                                  showPreferenceConsents
                                  showSpecialCategoryConsent={false}
                                />
                                {hasKnownOptionalPreferences ? (
                                  <div className="flex justify-end">
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => {
                                        setOptionalPreferencesVisibility(false);
                                      }}
                                    >
                                      Masquer
                                    </Button>
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <div className="flex items-center justify-between gap-4 rounded-3xl border border-brand-primary/10 bg-brand-paper/30 px-4 py-3">
                                <Typography variant="body2" sx={{ color: 'rgba(22, 51, 44, 0.72)', fontWeight: 500 }}>
                                  Preferences emails et analyse d'audience
                                </Typography>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    setOptionalPreferencesVisibility(true);
                                  }}
                                >
                                  Modifier
                                </Button>
                              </div>
                            )}

                            {requiresSpecialCategoryConsent ? (
                              <div className="mt-4 rounded-3xl border border-amber-200/80 bg-amber-50/60 p-4">
                                <OptionalConsentsSection
                                  value={effectiveConsentSelection}
                                  onChange={handleConsentSelectionChange}
                                  showPreferenceConsents={false}
                                  showSpecialCategoryConsent
                                  showTitle={false}
                                />
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <button
                        disabled={bookingLoading || bookingParticipantsCount <= 0}
                        type="submit"
                        className="btn-premium w-full !py-6 mt-12"
                      >
                        {bookingLoading ? 'SYNCHRONISATION...' : 'DEMANDE DE RÉSERVATION'}
                      </button>
                    </form>
                  ) : isBookingClosed ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center space-y-10">
                      <div className="w-24 h-24 rounded-full flex items-center justify-center border bg-orange-50 text-orange-800 border-orange-100">
                        <AlertCircle size={40} />
                      </div>
                      <div className="space-y-4">
                        <h3 className="text-4xl font-serif font-bold italic text-brand-primary">
                          Réservations clôturées
                        </h3>
                        <p className="text-brand-primary/40 font-serif text-xl italic leading-relaxed">
                          {outing.allowLateBookings
                            ? "La fenêtre de réservation de dernière minute est désormais fermée pour cette sortie."
                            : "Les réservations pour cette sortie sont fermées 24 heures avant le départ."}
                        </p>
                      </div>
                      <button onClick={() => navigate('/')} className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] border-b border-brand-primary pb-2 hover:opacity-60 transition-opacity text-brand-primary font-sans min-h-[44px]">
                        Voir les autres sorties
                      </button>
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col justify-center space-y-8">
                      <div className="text-center space-y-3">
                        <div className="flex items-center justify-center gap-2">
                          <h3 className="text-4xl font-serif font-bold italic text-brand-primary">Sortie complète</h3>
                          <HelpTrigger
                            articleId="booking.waitlist.overview"
                            category="booking.waitlist"
                            label="Aide sur la liste d'attente"
                          />
                        </div>
                        <p className="text-brand-primary/60 font-serif text-xl italic leading-relaxed">
                          Vous pouvez être prévenu si une place se libère.
                        </p>
                      </div>

                      <div className="rounded-[32px] border border-brand-primary/10 bg-brand-paper/40 p-6 space-y-6">
                        <p className="text-[11px] md:text-xs text-brand-primary/80 leading-relaxed font-sans font-bold uppercase tracking-[0.12em]">
                          Cette alerte ne réserve pas automatiquement une place.
                        </p>

                        <form onSubmit={handleWaitlistSubscribe} className="space-y-5">
                          <div className="group">
                            <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-75 mb-2 block text-brand-primary font-sans">Nom</label>
                            <input
                              required
                              type="text"
                              autoComplete="name"
                              placeholder="VOTRE NOM"
                              className="input-premium w-full bg-transparent border-b border-brand-primary/20 py-4 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all text-brand-primary font-sans placeholder:text-brand-primary/45"
                              value={effectiveWaitlistDisplayName}
                              onChange={(e) => setWaitlistForm((prev) => ({ ...prev, displayName: e.target.value }))}
                            />
                          </div>

                          <div className="group">
                            <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-75 mb-2 block text-brand-primary font-sans">E-mail</label>
                            <input
                              required
                              type="email"
                              autoComplete="email"
                              placeholder="VOTRE ADRESSE EMAIL"
                              className="input-premium w-full bg-transparent border-b border-brand-primary/20 py-4 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all text-brand-primary font-sans placeholder:text-brand-primary/45"
                              value={effectiveWaitlistEmail}
                              onChange={(e) => setWaitlistForm((prev) => ({ ...prev, email: e.target.value }))}
                            />
                          </div>

                          <div className="space-y-3 pt-2">
                            <label className="flex items-center gap-3 text-[12px] text-brand-primary/85 font-sans">
                              <input
                                type="checkbox"
                                checked={waitlistForm.notifyByEmail}
                                onChange={(e) => setWaitlistForm((prev) => ({ ...prev, notifyByEmail: e.target.checked }))}
                              />
                              Être prévenu par e-mail
                            </label>

                            {canOfferPushWaitlist ? (
                              <label className="flex items-center gap-3 text-[12px] text-brand-primary/85 font-sans">
                                <input
                                  type="checkbox"
                                  checked={effectiveWaitlistNotifyByPush}
                                  onChange={(e) => setWaitlistForm((prev) => ({ ...prev, notifyByPush: e.target.checked }))}
                                />
                                Être prévenu par notification push
                              </label>
                            ) : null}
                          </div>

                          <label className="flex items-start gap-3 text-[12px] text-brand-primary/85 font-sans leading-relaxed">
                            <input
                              type="checkbox"
                              checked={waitlistForm.consentAccepted}
                              onChange={(e) => setWaitlistForm((prev) => ({ ...prev, consentAccepted: e.target.checked }))}
                              className="mt-1"
                            />
                            J'accepte d'être contacté uniquement au sujet des places disponibles pour cette sortie.
                          </label>

                          {waitlistFeedback ? (
                            <div className={`rounded-2xl px-4 py-3 text-[12px] font-sans leading-relaxed ${
                              waitlistFeedback.type === 'success'
                                ? 'bg-emerald-50 text-emerald-900 border border-emerald-100'
                                : waitlistFeedback.type === 'info'
                                  ? 'bg-sky-50 text-sky-900 border border-sky-100'
                                  : 'bg-red-50 text-red-900 border border-red-100'
                            }`}>
                              {waitlistFeedback.message}
                            </div>
                          ) : null}

                          <button
                            type="submit"
                            disabled={waitlistLoading}
                            className="btn-premium w-full"
                          >
                            {waitlistLoading ? 'ENREGISTREMENT...' : 'Me prévenir si une place se libère'}
                          </button>
                        </form>

                        {isAuthenticated && waitlistSubscribed && Boolean(waitlistEntryId) ? (
                          <button
                            type="button"
                            onClick={handleWaitlistUnsubscribe}
                            disabled={waitlistLoading}
                            className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] border-b border-brand-primary pb-2 hover:opacity-60 transition-opacity text-brand-primary font-sans min-h-[44px]"
                          >
                            Me désinscrire de cette alerte
                          </button>
                        ) : null}
                      </div>

                      <button onClick={() => navigate('/')} className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] border-b border-brand-primary pb-2 hover:opacity-60 transition-opacity text-brand-primary font-sans min-h-[44px] self-center">
                        Voir les autres sorties
                      </button>
                    </div>
                  )}
                </motion.div>
              )}

              {paymentStep === 'mollie' && (
                <motion.div 
                  key="mollie"
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  className="flex-1 flex flex-col items-center justify-center text-center space-y-12 relative z-10"
                >
                  <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center text-blue-600 border border-blue-100">
                    <CreditCard size={48} />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-4xl font-serif font-bold italic text-brand-primary">Paiement Sécurisé Mollie</h3>
                    <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-55 text-brand-primary font-sans">Réglé via carte bancaire ou autre moyen sécurisé</p>
                  </div>
                  <div className="bg-brand-paper p-8 rounded-[40px] w-full space-y-4 text-brand-primary font-sans">
                    <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-60">
                      <span>Total à régler</span>
                      <span>{bookingTotalLabel}</span>
                    </div>
                  </div>
                  <button 
                    onClick={processBooking}
                    disabled={bookingLoading}
                    className="w-full bg-blue-600 text-white py-6 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.2em] hover:bg-blue-700 transition-all shadow-2xl shadow-blue-600/20 font-sans disabled:opacity-70 disabled:cursor-not-allowed min-h-[48px]"
                  >
                    {bookingLoading ? 'REDIRECTION EN COURS...' : 'CONFIRMER LE PAIEMENT'}
                  </button>
                  <button 
                    onClick={() => setPaymentStep('form')}
                    className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-60 hover:opacity-100 transition-opacity text-brand-primary font-sans min-h-[44px]"
                  >
                    Annuler et modifier
                  </button>
                </motion.div>
              )}

              {paymentStep === 'bank_transfer' && (
                <motion.div 
                  key="bank_transfer"
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  className="flex-1 flex flex-col items-center justify-center text-center space-y-12 relative z-10"
                >
                  <div className="w-24 h-24 bg-brand-accent/10 rounded-full flex items-center justify-center text-brand-accent border border-brand-accent/10">
                    <Clock size={48} />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-4xl font-serif font-bold italic text-brand-primary">Presque fini !</h3>
                    <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 text-brand-primary font-sans leading-relaxed">
                      Votre demande de réservation a été enregistrée. Elle sera <span className="text-brand-accent">définitivement confirmée</span> dès réception de votre virement bancaire.
                    </p>
                  </div>
                  
                  <div className="bg-brand-paper p-8 rounded-[40px] w-full space-y-6 text-brand-primary font-sans border border-brand-accent/10">
                    <BankTransferInstructions
                      iban={bankTransferIban}
                      amount={returnBookingInfo?.amount ?? bookingTotalAmount}
                      reference={bankCommunication}
                      participantsCount={displayedBookingSnapshot?.participantsCount}
                      bookingLines={displayedBookingSnapshot?.bookingLines}
                      moneyTotals={displayedBookingSnapshot?.moneyTotals}
                    />
                  </div>

                  <div className="flex items-center gap-4 text-left bg-emerald-50/60 p-6 rounded-2xl border border-emerald-100/70">
                    <Info size={16} className="text-emerald-700 flex-shrink-0" />
                    <p className="text-[11px] md:text-xs text-emerald-900 leading-relaxed font-sans font-bold uppercase tracking-[0.12em]">
                      Info : Vous avez reçu un e-mail avec toutes les instructions pour effectuer le paiement ultérieurement. Vous pouvez fermer cette fenêtre et retrouver ces informations dans votre e-mail.
                    </p>
                  </div>

                  <div className="flex items-center gap-4 text-left bg-orange-50/50 p-6 rounded-2xl border border-orange-100/50">
                    <Info size={16} className="text-orange-600 flex-shrink-0" />
                    <p className="text-[11px] md:text-xs text-orange-800 leading-relaxed font-sans font-bold uppercase tracking-[0.12em]">
                      Note : Si le paiement n'est pas reçu 24h avant l'expédition, la réservation sera automatiquement annulée.
                    </p>
                  </div>

                  <button 
                    onClick={() => navigate('/')}
                    className="btn-premium w-full"
                  >
                    Retour à l'accueil
                  </button>
                </motion.div>
              )}

              {paymentStep === 'success' && (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex-1 flex flex-col items-center justify-center text-center space-y-12 relative z-10"
                >
                  <div className="w-32 h-32 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-600 border border-emerald-100">
                    <CheckCircle size={64} />
                  </div>
                  <div className="space-y-6">
                    <h3 className="text-5xl font-serif font-bold italic text-brand-primary">C'est réservé !</h3>
                    <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-60 leading-loose max-w-xs mx-auto text-brand-primary font-sans">
                      {successName ? <>Merci {successName}.<br /></> : <>Merci.<br /></>}
                      {successEmail
                        ? <>Un email de confirmation a été envoyé à {successEmail}.</>
                        : <>Un email de confirmation vous a été envoyé.</>}
                    </p>
                  </div>
                  {displayedBookingSnapshot ? (
                    <div className="w-full bg-brand-paper p-8 rounded-[40px] border border-emerald-100/70 text-left">
                      <BookingPricingSummary
                        participantsCount={displayedBookingSnapshot.participantsCount}
                        bookingLines={displayedBookingSnapshot.bookingLines}
                        moneyTotals={displayedBookingSnapshot.moneyTotals}
                        amount={displayedBookingSnapshot.amount}
                      />
                    </div>
                  ) : null}
                  <button 
                    onClick={() => navigate('/')}
                    className="btn-premium w-full"
                  >
                    Retour à l'accueil
                  </button>
                </motion.div>
              )}

              {paymentStep === 'verifying' && (
                <motion.div 
                  key="verifying"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex-1 flex flex-col items-center justify-center text-center space-y-12 relative z-10"
                >
                  <div className="w-32 h-32 bg-brand-primary/5 rounded-full flex items-center justify-center text-brand-primary border border-brand-primary/10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
                  </div>
                  <div className="space-y-6">
                    <h3 className="text-4xl font-serif font-bold italic text-brand-primary">Validation en cours</h3>
                    <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-60 leading-loose max-w-xs mx-auto text-brand-primary font-sans">
                      {verificationMessage}
                    </p>
                    {verificationHint && (
                      <div className="max-w-md mx-auto bg-brand-paper border border-brand-primary/10 rounded-[24px] px-6 py-5 text-left">
                        <p className="text-[11px] md:text-sm leading-relaxed text-brand-primary/75 font-sans">
                          {verificationHint}
                        </p>
                      </div>
                    )}
                  </div>
                  {verificationHint && (
                    <button
                      onClick={() => window.location.reload()}
                      className="btn-premium w-full"
                    >
                      Actualiser le statut
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      {/* Mobile CTA flottant - visible uniquement sous lg, ne s'affiche qu'au step formulaire initial */}
      {canShowMobileBookingCta && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pt-3 pb-[max(16px,env(safe-area-inset-bottom))] bg-white/95 backdrop-blur-xl border-t border-brand-primary/5 shadow-xl">
          <button
            type="button"
            onClick={() => {
              document.getElementById('booking-widget')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="btn-premium w-full"
          >
            Réserver - {bookingFormPricing ? bookingTotalLabel : (pricingDisplay?.homePrimaryLabel || 'Réserver')}
          </button>
        </div>
      )}
      <NotificationPrompt 
        avoidBottomOverlap={canShowMobileBookingCta}
        show={showNotificationPrompt}
        onAccept={async () => {
          const granted = await requestPermission();
          setShowNotificationPrompt(false);
          if (granted) {
            toast.success("Notifications activées !");
          }
        }}
        onDecline={() => {
          setShowNotificationPrompt(false);
          toast("Pas de souci, vous pouvez changer d'avis plus tard.", { icon: '🌿' });
        }}
      />
    </motion.div>
  );
}